import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiError } from "../lib/apiClient";
import { CertType } from "../lib/types";

type Props = { mode: "create" | "edit" };

type CertTypesResponse = { cert_types: CertType[] };

export default function CompletionFormPage({ mode }: Props) {
  const { pin = "", id = "" } = useParams();
  const navigate = useNavigate();

  const [certTypes, setCertTypes] = useState<CertType[]>([]);
  const [certTypeSlug, setCertTypeSlug] = useState("");
  const [certTypeName, setCertTypeName] = useState("");
  const [acquiredOn, setAcquiredOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [name, setName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [attachMode, setAttachMode] = useState<"none" | "url" | "file">("none");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode !== "create") return;
    apiFetch<CertTypesResponse>("/api/v1/cert_types")
      .then(r => setCertTypes(r.cert_types ?? []))
      .catch(e => setError(e instanceof Error ? e.message : "Failed to load cert types"));
  }, [mode]);

  // Pre-fill the form from the existing completion when editing.
  useEffect(() => {
    if (mode !== "edit" || !id) return;
    apiFetch<{
      acquired_on: string | null;
      expires_on: string | null;
      name: string | null;
      files: string[];
      cert_type_slug: string | null;
      cert_type_name: string | null;
    }>(`/api/v1/completions/${id}`)
      .then(r => {
        if (r.acquired_on) setAcquiredOn(r.acquired_on);
        if (r.expires_on) setExpiresOn(r.expires_on);
        if (r.name) setName(r.name);
        if (r.cert_type_slug) setCertTypeSlug(r.cert_type_slug);
        if (r.cert_type_name) setCertTypeName(r.cert_type_name);
        if (r.files && r.files.length > 0) {
          setFileUrl(r.files[0]);
          setAttachMode("url");
        }
      })
      .catch(e => {
        setError(e instanceof Error ? e.message : "Failed to load completion");
      });
  }, [mode, id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setErrors({});

    try {
      if (mode === "create") {
        if (attachMode === "file" && file) {
          const form = new FormData();
          form.set("registrant_pin", pin);
          form.set("cert_type_slug", certTypeSlug);
          form.set("acquired_on", acquiredOn);
          if (expiresOn) form.set("expires_on", expiresOn);
          if (name) form.set("name", name);
          form.set("file", file);
          await apiFetch("/api/v1/completions", { method: "POST", multipart: form });
        } else {
          const body: Record<string, unknown> = {
            registrant_pin: pin,
            cert_type_slug: certTypeSlug,
            acquired_on: acquiredOn,
          };
          if (expiresOn) body.expires_on = expiresOn;
          if (name) body.name = name;
          if (attachMode === "url" && fileUrl) body.file_url = fileUrl;
          await apiFetch("/api/v1/completions", { method: "POST", body });
        }
        navigate(`/registrants/${pin}`);
      } else {
        const body: Record<string, unknown> = {};
        if (acquiredOn) body.acquired_on = acquiredOn;
        if (expiresOn) body.expires_on = expiresOn;
        if (name) body.name = name;
        if (fileUrl) body.file_url = fileUrl;
        await apiFetch(`/api/v1/completions/${id}`, { method: "PUT", body });
        navigate(-1);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(`${e.code}: ${e.message}`);
        setErrors(e.errors ?? {});
      } else {
        setError(e instanceof Error ? e.message : "Request failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <Link to={mode === "create" ? `/registrants/${pin}` : "/registrants"} className="text-sm text-brand-700 hover:underline">← Back</Link>
        <h1 className="text-xl font-semibold mt-1">
          {mode === "create" ? `New completion for ${pin}` : `Edit completion #${id}`}
        </h1>
        {mode === "edit" && certTypeName && (
          <div className="text-sm text-slate-500 mt-1">
            {certTypeName} <span className="mono text-xs">({certTypeSlug})</span>
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
          {error}
          {Object.keys(errors).length > 0 && (
            <ul className="mt-1 list-disc list-inside">
              {Object.entries(errors).map(([k, v]) => <li key={k}><b>{k}:</b> {v}</li>)}
            </ul>
          )}
        </div>
      )}

      <form onSubmit={submit} className="space-y-3 bg-white border border-slate-200 rounded p-4">
        {mode === "create" && (
          <Field label="Cert type" error={errors.cert_type_slug}>
            <select
              className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
              value={certTypeSlug}
              onChange={e => setCertTypeSlug(e.target.value)}
              required
            >
              <option value="">— select —</option>
              {certTypes.map(ct => (
                <option key={ct.slug} value={ct.slug}>
                  {ct.name} ({ct.category ?? "uncategorised"})
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Acquired on" error={errors.acquired_on}>
          <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={acquiredOn} onChange={e => setAcquiredOn(e.target.value)} required={mode === "create"} />
        </Field>

        <Field label="Expires on (optional)" error={errors.expires_on}>
          <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={expiresOn} onChange={e => setExpiresOn(e.target.value)} />
        </Field>

        <Field label="Display name (optional)">
          <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fire Safety 2026" />
        </Field>

        <Field label="Evidence file">
          <div className="space-y-2">
            <div className="flex gap-4 text-sm">
              <label><input type="radio" name="attach" checked={attachMode === "none"} onChange={() => setAttachMode("none")} /> None</label>
              <label><input type="radio" name="attach" checked={attachMode === "url"} onChange={() => setAttachMode("url")} /> file_url</label>
              <label><input type="radio" name="attach" checked={attachMode === "file"} onChange={() => setAttachMode("file")} /> Upload (multipart)</label>
            </div>
            {attachMode === "url" && (
              <input type="url" className="w-full border border-slate-300 rounded px-2 py-1 text-sm mono" placeholder="https://org.example.com/cert.pdf" value={fileUrl} onChange={e => setFileUrl(e.target.value)} />
            )}
            {attachMode === "file" && (
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={e => setFile(e.target.files?.[0] ?? null)} />
            )}
            {errors.file && <div className="text-red-600 text-xs">{errors.file}</div>}
            {errors.file_url && <div className="text-red-600 text-xs">{errors.file_url}</div>}
          </div>
        </Field>

        <div className="flex gap-2 pt-2">
          <button type="submit" className="bg-brand-600 text-white rounded px-3 py-2 text-sm" disabled={submitting}>
            {submitting ? "Submitting…" : mode === "create" ? "Create completion" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 mb-1">{label}</div>
      {children}
      {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
    </label>
  );
}
